import React, { useState, useRef } from 'react';
import { Button, ListGroup, Badge } from 'react-bootstrap';

interface DocumentUploaderProps {
    onFileChange: (files: File[]) => void;
    maxFiles?: number;
    acceptedFileTypes?: string;
    existingDocuments?: { id: string; name: string }[];
    disabled?: boolean;
}

export const DocumentUploader: React.FC<DocumentUploaderProps> = ({
    onFileChange,
    maxFiles = 5,
    acceptedFileTypes = '.pdf,.doc,.docx,.txt,.rtf',
    existingDocuments = [],
    disabled = false
}) => {
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files || !files.length) return;

        const newFiles = Array.from(files);

        // Check if adding new files exceeds the maximum
        if (selectedFiles.length + newFiles.length + existingDocuments.length > maxFiles) {
            alert(`You can only upload a maximum of ${maxFiles} files.`);
            return;
        }

        // Add new files to state
        const updatedFiles = [...selectedFiles, ...newFiles];
        setSelectedFiles(updatedFiles);

        // Notify parent component
        onFileChange(updatedFiles);

        // Reset file input
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    return (
        <div className="document-uploader">
            <input
                type="file"
                multiple
                ref={fileInputRef}
                onChange={handleFileSelect}
                style={{ display: 'none' }}
                accept={acceptedFileTypes}
            />

            <div className="d-grid">
                <Button
                    variant="outline-primary"
                    onClick={() => fileInputRef.current?.click()}
                    className="mb-3"
                    disabled={selectedFiles.length + existingDocuments.length >= maxFiles || disabled}
                >
                    <i className="bi bi-upload me-2"></i>
                    Upload Documents
                </Button>
            </div>

            {selectedFiles.length === 0 && existingDocuments.length === 0 && (
                <div className="text-center py-4 border rounded bg-light">
                    <i className="bi bi-file-earmark-arrow-up fs-3 text-muted"></i>
                    <p className="text-muted mt-2 mb-0">
                        Drag files here or click the upload button
                    </p>
                </div>
            )}
        </div>
    );
};

export default DocumentUploader;